import { useState } from "react";
import "./self_identification_form.scss";
import { Body, Link, SectionTitle } from "@sprout/typography";
import { Collapsible } from "@sprout/collapsible";
import { SelfIdentificationDetails } from "@components/self_identification_details";
import useTranslation from "@/hooks/use_translation";
import { SelfIdentification, SelfIdentificationProps } from "@/utils/self_identification";
import { type Option, SingleSelect } from "@sprout/select";
import { TertiaryButton } from "@sprout/buttons";

const findSelectedHispanicOrLatinoOption = (
  options: Option[],
  value?: SelfIdentification | null,
): Option | null => {
  if (value?.race == null) {
    return null;
  }

  const selectedOption = options.find((option) => option.value === value.race);

  if (selectedOption) {
    return selectedOption;
  }

  return options.find((option) => option.value === "") || null;
};

const findSelectedOption = (options: Option[], value?: string | number) => {
  return options.find((option) => option.value === value) || null;
};

export const SelfIdentificationForm = (props: SelfIdentificationProps) => {
  const { details, onChange, options, value } = props;
  const { t } = useTranslation("user");

  const [showMainDetails, setShowMainDetails] = useState(true);
  const [showVeteranDetails, setShowVeteranDetails] = useState(false);
  const [showDisabilityDetails, setShowDisabilityDetails] = useState(false);
  const [showRace, setShowRace] = useState(
    value?.race === "" || options.raceOptions.some((option) => option.value === value?.race),
  );

  const handleChange = (key: keyof SelfIdentification, selectedOption: Option | null) => {
    onChange({
      ...((value || {}) as SelfIdentification),
      [key]: selectedOption ? (selectedOption as Option).value : null,
    });
  };

  const onHispanicChange = (updatedValue: Option | null) => {
    handleChange("race", updatedValue);
    if ((updatedValue as Option)?.value === "") {
      setShowRace(true);
    } else {
      setShowRace(false);
    }
  };

  return (
    <div className="self-identification">
      <div className="self-identification--section">
        <SectionTitle large>{details.title}</SectionTitle>
        <Body metadata>{details.helperText}</Body>
        <div className="self-identification--collapsible">
          <Collapsible show={showMainDetails}>
            <SelfIdentificationDetails segments={details.example} />
          </Collapsible>
          <TertiaryButton onClick={() => setShowMainDetails(!showMainDetails)}>
            {showMainDetails
              ? t("selfIdentification.hideExample")
              : t("selfIdentification.showExample")}
          </TertiaryButton>
        </div>
        <SingleSelect
          label={t("selfIdentification.gender")}
          onChange={(updatedValue) => handleChange("gender", updatedValue)}
          options={options.genderOptions}
          value={findSelectedOption(options.genderOptions, value?.gender)}
          clearable
        />
        <SingleSelect
          label={t("selfIdentification.hispanicOrLatino")}
          onChange={(updatedValue) => onHispanicChange(updatedValue)}
          options={options.hispanicOrLatinoOptions}
          value={findSelectedHispanicOrLatinoOption(options.hispanicOrLatinoOptions, value)}
          clearable
        />
        {showRace && (
          <SingleSelect
            label={t("selfIdentification.raceSelect")}
            onChange={(updatedValue) => handleChange("race", updatedValue)}
            options={options.raceOptions}
            value={findSelectedOption(options.raceOptions, value?.race)}
            clearable
          />
        )}
        <Link href="https://boards.cdn.greenhouse.io/docs/RaceEthnicityDefinitions.pdf" external>
          {"Race & ethnicity definitions"}
        </Link>
      </div>
      <div className="self-identification--section">
        <Body medium>{details.veteran.title}</Body>
        <div className="self-identification--collapsible">
          <Collapsible show={showVeteranDetails}>
            <SelfIdentificationDetails segments={details.veteran.example} />
          </Collapsible>
          <TertiaryButton onClick={() => setShowVeteranDetails(!showVeteranDetails)}>
            {showVeteranDetails
              ? t("selfIdentification.hideExample")
              : t("selfIdentification.showExample")}
          </TertiaryButton>
        </div>
        <SingleSelect
          label={t("selfIdentification.veteranStatus")}
          onChange={(updatedValue) => handleChange("veteranStatus", updatedValue)}
          options={options.veteranOptions}
          value={findSelectedOption(options.veteranOptions, value?.veteranStatus)}
          clearable
        />
      </div>
      <div className="self-identification--section">
        <Body medium>{details.disability.title}</Body>
        <div className="self-identification--collapsible">
          <Collapsible show={showDisabilityDetails}>
            <SelfIdentificationDetails segments={details.disability.example} />
          </Collapsible>
          <TertiaryButton onClick={() => setShowDisabilityDetails(!showDisabilityDetails)}>
            {showDisabilityDetails
              ? t("selfIdentification.hideExample")
              : t("selfIdentification.showExample")}
          </TertiaryButton>
        </div>
        <SingleSelect
          label={t("selfIdentification.disabilityStatus")}
          onChange={(updatedValue) => handleChange("disabilityStatus", updatedValue)}
          options={options.disabilityOptions}
          value={findSelectedOption(options.disabilityOptions, value?.disabilityStatus)}
          clearable
        />
      </div>
    </div>
  );
};
